import { useContext, useState } from "react";

import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import type { Week } from "api/services/treatment/schemas/ScheduleWeek";
import type { CalculatedDurationWeeks } from "api/services/treatment/schemas/Treatment";
import { EditIcon } from "assets";
import { useProfileContext } from "contexts/ProfileContext";
import { BaseButton } from "shared/atoms/BaseButton";
import { Hidden } from "shared/atoms/Hidden";
import { IconButton } from "shared/atoms/IconButton";
import { Pagination } from "shared/atoms/Pagination";
import { CurrentPatientContext } from "utils/contexts";
import { useLocalizedDate } from "utils/date";
import { userIsTherapist } from "utils/profile/profileHelper";

import { ChangeLevels } from "../ChangeLevels";
import { DownloadProtocolButton } from "../DownloadProtocol/DownloadProtocolButton";

type ProtocolHeaderProps = {
  calculated_duration_weeks?: CalculatedDurationWeeks;
  empty?: boolean;
  hasOverdueRequiredActivities: boolean;
  week: Week;
  onCurrentClick: () => void;
  onPageChange: (direction: string) => void;
};

export const ProtocolHeader: React.FC<ProtocolHeaderProps> = ({
  calculated_duration_weeks,
  empty = false,
  hasOverdueRequiredActivities,
  week,
  onPageChange,
  onCurrentClick,
}) => {
  const { t } = useTranslation();
  const { format, parseISO } = useLocalizedDate();
  const navigate = useNavigate();
  const [openTooltip, setOpenTooltip] = useState(false);
  const { patient: currentPatient, setShowSelectTreatmentSidebar } = useContext(CurrentPatientContext);
  const { profile } = useProfileContext();

  const isTherapist = userIsTherapist(profile);

  // UserCourse AKA SGC (Supply side Generated Content) aka "Physio created treatment programs". In stage, user 407850 (PT: 173406) has this enabed
  const userCourseEnabled = currentPatient.current_course_strategy === "custom";

  // Cookie override is a temporary escape hatch for testing on production — remove once verified
  const showDownloadProtocolButton =
    currentPatient.ce_structure === "rtm" ||
    currentPatient.ce_structure === "hybrid" ||
    !!Cookies.get("showDownloadProtocolButton");

  return (
    <Container data-testid="protocol-header">
      <Header>
        <Wrapper>
          <Hidden type="belowTablet">
            <BaseButton
              text={t("patients.protocol.current")}
              variant="secondary"
              disabled={week.week_number === currentPatient.week || empty}
              onClick={() => onCurrentClick()}
              style={{ margin: "0 16px 0 0 " }}
            />
          </Hidden>
          <Pagination
            pageInfo={{
              hasNextPage:
                currentPatient.schedule_version === 2
                  ? week.week_number < Math.max(calculated_duration_weeks || 52, currentPatient.week)
                  : week.week_number < currentPatient.week + 2,
              hasPreviousPage: week.week_number > 0,
            }}
            onPageChange={direction => onPageChange(direction)}
          />
        </Wrapper>

        <Text $empty={empty}>
          {week.week_number !== null && (
            <MainText data-testid="protocol-week-title">
              {`${t("common.week_num", { number: week.week_number.toString() })} `}
              {week.week_number === currentPatient.week &&
                (hasOverdueRequiredActivities ? (
                  <span data-testid="awaiting-completion-label">({t("patients.protocol.awaiting_completion")})</span>
                ) : (
                  <span data-testid="current-label">{`(${t("patients.protocol.current").toLowerCase()})`}</span>
                ))}
            </MainText>
          )}
          <SubText>
            {week.start_date &&
              week.end_date &&
              t("common.range", {
                start: format(parseISO(week.start_date), t("common.date.longNoYear")),
                end: format(parseISO(week.end_date), "PP"),
              })}
          </SubText>
        </Text>

        {isTherapist && !userCourseEnabled && (
          <Wrapper $right>
            <IconButton
              label={t("patients.protocol.change_levels")}
              hideLabel="belowTablet"
              onClick={event => {
                event.stopPropagation();
                setOpenTooltip(true);
              }}
              dataTestId="patients-protocol-change-levels-button"
            >
              <EditIcon />
            </IconButton>
            {openTooltip && <ChangeLevels open closeCallback={() => setOpenTooltip(false)} />}
          </Wrapper>
        )}
        {showDownloadProtocolButton && (
          <Wrapper $right>
            <DownloadProtocolButton
              patientId={currentPatient.id}
              patientName={[currentPatient.first_name, currentPatient.last_name].filter(Boolean).join(" ")}
              patientPersonalNumber={currentPatient.personal_number ?? null}
              patientBirthday={currentPatient.birthday}
              scheduleVersion={currentPatient.schedule_version}
            />
          </Wrapper>
        )}
        {isTherapist && userCourseEnabled && (
          <Wrapper $right>
            <BaseButton
              text={
                currentPatient.awaiting_treatment_ready ? t("usercourse.create_course") : t("usercourse.edit_course")
              }
              icon="pen"
              onClick={event => {
                event.stopPropagation();
                if (currentPatient.schedule_version === 2) {
                  if (currentPatient.awaiting_treatment_ready) {
                    setShowSelectTreatmentSidebar(true);
                  } else {
                    navigate(`/patients/${currentPatient.id}/treatment/edit`);
                  }
                } else {
                  navigate(
                    currentPatient.awaiting_treatment_ready
                      ? `/patients/${currentPatient.id}/usercourse/new`
                      : `/patients/${currentPatient.id}/usercourse`
                  );
                }
              }}
            />
          </Wrapper>
        )}
      </Header>
    </Container>
  );
};

const Container = styled.div`
  border-bottom: ${({ theme }) => theme.semantic.container.border};
  width: 100%;
  display: flex;
  justify-content: center;
  background: linear-gradient(180deg, #ffffff 0%, #fafbfc 100%);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  position: sticky;
  top: 0;
  z-index: 10;
`;

const Header = styled.div`
  max-width: 1400px;
  box-sizing: border-box;
  width: 100%;
  min-height: 80px;
  padding: 16px ${props => props.theme.spacing.S_30};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;

  ${props => props.theme.belowBreakpoint} {
    min-height: 64px;
    padding: 12px 16px;
    gap: 12px;
  }
`;

const Wrapper = styled.div<{ $right?: boolean }>`
  display: flex;
  ${props => (props.$right ? `margin-left: ${props.theme.spacing.S_20}` : `margin-right: ${props.theme.spacing.S_20}`)};
  ${props => props.$right && "position: relative;"}
`;

const Text = styled.div<{ $empty?: boolean }>`
  opacity: ${props => (props.$empty ? 0 : 1)};
  transition: opacity 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow: hidden;
  gap: 4px;
  flex: 1;
  min-width: 0;

  ${props => props.theme.belowBreakpoint} {
    align-items: center;
  }
`;

const MainText = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #1a2332;
  letter-spacing: -0.02em;
  line-height: 1.3;

  ${props => props.theme.belowBreakpoint} {
    font-size: 18px;
    text-align: center;
  }

  span {
    color: #0066cc;
    font-weight: 600;
  }

  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const SubText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #8391a1;
  letter-spacing: -0.01em;

  ${props => props.theme.belowBreakpoint} {
    font-size: 13px;
    text-align: center;
  }

  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;
